import { motion } from 'framer-motion';
import { BadgeCheck, AlertCircle } from 'lucide-react';
import { ConfidenceBar } from '@/components/profiler/ConfidenceBar';
import { IdentifierClusterGrid } from '@/components/profiler/IdentifierClusterGrid';
import { PivotChain } from '@/components/profiler/PivotChain';
import type { DossierSectionProps } from './types';

// ── helpers ───────────────────────────────────────────────────────────────────

function VerifiedBadge({ verified }: { verified?: boolean }) {
  if (verified === undefined) return null;
  return verified ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[8px] font-mono font-bold"
      style={{ background: '#00C85318', color: '#00C853', border: '1px solid #00C85330' }}>
      <BadgeCheck size={9} /> VERIFIED
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[8px] font-mono font-bold"
      style={{ background: '#5A7A9A18', color: '#5A7A9A', border: '1px solid #5A7A9A30' }}>
      <AlertCircle size={9} /> UNVERIFIED
    </span>
  );
}

function FieldRow({ label, value, mono = false }: { label: string; value?: string; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between py-2.5" style={{ borderBottom: '1px solid #0F1928' }}>
      <span style={{ color: '#5A7A9A', fontSize: 10, fontFamily: 'monospace' }}>{label}</span>
      <span
        style={{
          color: value ? '#DCE8F5' : '#283A50',
          fontSize: 10,
          fontFamily: mono ? 'monospace' : undefined,
          fontWeight: mono ? 600 : undefined,
        }}
      >
        {value ?? '—'}
      </span>
    </div>
  );
}

// ── main component ────────────────────────────────────────────────────────────

export function PIIIdentity({ report, pipeline, cluster, onDiscard }: DossierSectionProps) {
  const identity = pipeline?.identity;
  const effectiveCluster = cluster ?? pipeline?.cluster ?? report.identifier_cluster;

  const passportVariants = effectiveCluster?.variants.filter(v => v.type === 'PASSPORT') ?? [];
  const nameVariants = effectiveCluster?.variants.filter(v => v.type === 'NAME') ?? [];
  const relationVariants = effectiveCluster?.variants.filter(v => v.type === 'RELATION') ?? [];

  const canonicalName = effectiveCluster?.canonical ?? report.seed ?? '—';
  const allNames = effectiveCluster?.all_names ?? [];

  // Parse relation variants: format "RELATION:TYPE:Name" or just use value
  interface RelationEntry { relType: string; name: string; source: string }
  const relations: RelationEntry[] = relationVariants.map(v => {
    const parts = v.value.split(':');
    if (parts.length >= 3) {
      return { relType: parts[1], name: parts.slice(2).join(':'), source: v.source };
    }
    return { relType: 'ASSOCIATE', name: v.value, source: v.source };
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25 }}
      className="flex flex-col gap-5"
    >
      {/* Government identity card */}
      <div
        style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 8, overflow: 'hidden' }}
      >
        <div
          style={{ background: '#090D13', borderBottom: '1px solid #182030', padding: '10px 14px' }}
          className="flex items-center justify-between"
        >
          <span style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.15em' }}>
            GOVERNMENT IDENTITY
          </span>
          <div className="flex items-center gap-2">
            <VerifiedBadge verified={identity?.pan_verified} />
            {identity?.voter_verified && <VerifiedBadge verified={true} />}
          </div>
        </div>
        <div className="px-4">
          <FieldRow label="NAME ON PAN" value={identity?.name_on_pan ?? canonicalName} mono />
          <FieldRow label="DATE OF BIRTH" value={identity?.dob} />
          <FieldRow label="NATIONALITY" value={identity?.nationality ?? 'Indian'} />
          <FieldRow label="PAN" value={
            effectiveCluster?.variants.find(v => v.type === 'PAN')?.value
          } mono />
          <FieldRow label="VOTER ID" value={identity?.voter_id} mono />
          <FieldRow
            label="VOTER ID VERIFIED"
            value={identity?.voter_verified !== undefined ? (identity.voter_verified ? 'YES' : 'NO') : undefined}
          />
          <FieldRow label="DRIVER'S LICENCE" value={identity?.dl_number} mono />
          <FieldRow
            label="DL VERIFIED"
            value={identity?.dl_verified !== undefined ? (identity.dl_verified ? 'YES' : 'NO') : undefined}
          />
        </div>
      </div>

      {/* Passport records */}
      {passportVariants.length > 0 && (
        <div>
          <p style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.15em', marginBottom: 10 }}>
            PASSPORT RECORDS
          </p>
          <div className="flex flex-col gap-2">
            {passportVariants.map((v, i) => (
              <div
                key={i}
                style={{
                  background: '#0C1118',
                  border: '1px solid #182030',
                  borderLeft: '3px solid #FF6B1A',
                  borderRadius: 8,
                  padding: '10px 12px',
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <span style={{ color: '#DCE8F5', fontFamily: 'monospace', fontWeight: 700, fontSize: 12 }}>
                    {v.value}
                  </span>
                  <span
                    style={{
                      background: '#FF6B1A15',
                      color: '#FF6B1A',
                      border: '1px solid #FF6B1A30',
                      fontSize: 8,
                      fontFamily: 'monospace',
                      padding: '1px 6px',
                      borderRadius: 99,
                    }}
                  >
                    PASSPORT
                  </span>
                </div>
                <ConfidenceBar value={v.confidence} color="#FF6B1A" height={3} showLabel />
                <div className="flex items-center justify-between mt-2">
                  <span style={{ color: '#283A50', fontSize: 8, fontFamily: 'monospace' }}>{v.source}</span>
                  <span style={{ color: '#F0B429', fontSize: 8, fontFamily: 'monospace' }}>
                    * from breach data — treat with caution
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Name variants chip grid */}
      <div>
        <p style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.15em', marginBottom: 10 }}>
          NAME VARIANTS
        </p>
        <div className="flex flex-wrap gap-2">
          {/* Canonical first */}
          <span
            style={{
              background: '#00C8FF18',
              color: '#00C8FF',
              border: '1px solid #00C8FF40',
              fontSize: 10,
              fontFamily: 'monospace',
              fontWeight: 700,
              padding: '4px 10px',
              borderRadius: 6,
            }}
          >
            {canonicalName}
          </span>
          {/* Other names from all_names */}
          {allNames.filter(n => n !== canonicalName).map((name, i) => (
            <span
              key={`allname-${i}`}
              style={{
                background: '#0C1118',
                color: '#DCE8F5',
                border: '1px solid #182030',
                fontSize: 10,
                fontFamily: 'monospace',
                padding: '4px 10px',
                borderRadius: 6,
              }}
            >
              {name}
            </span>
          ))}
          {/* Name-type variants not in all_names */}
          {nameVariants.filter(v => v.value !== canonicalName && !allNames.includes(v.value)).map((v, i) => (
            <span
              key={`namevar-${i}`}
              style={{
                background: '#0C1118',
                color: '#DCE8F5',
                border: '1px solid #182030',
                fontSize: 10,
                fontFamily: 'monospace',
                padding: '4px 10px',
                borderRadius: 6,
              }}
            >
              {v.value}
            </span>
          ))}
          {allNames.length === 0 && nameVariants.length === 0 && canonicalName === '—' && (
            <span style={{ color: '#283A50', fontSize: 10, fontFamily: 'monospace' }}>No name variants found</span>
          )}
        </div>
      </div>

      {/* Family & Associates */}
      {relations.length > 0 && (
        <div>
          <p style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.15em', marginBottom: 10 }}>
            FAMILY AND ASSOCIATES
          </p>
          <div className="grid grid-cols-2 gap-2">
            {relations.map((rel, i) => (
              <div
                key={i}
                style={{
                  background: '#0C1118',
                  border: '1px solid #182030',
                  borderLeft: '3px solid #C026D3',
                  borderRadius: 8,
                  padding: '8px 12px',
                }}
              >
                <span
                  style={{
                    background: '#C026D318',
                    color: '#C026D3',
                    border: '1px solid #C026D330',
                    fontSize: 8,
                    fontFamily: 'monospace',
                    fontWeight: 700,
                    padding: '1px 6px',
                    borderRadius: 99,
                    display: 'inline-block',
                    marginBottom: 4,
                  }}
                >
                  {rel.relType}
                </span>
                <div style={{ color: '#DCE8F5', fontSize: 11, fontWeight: 600 }}>{rel.name}</div>
                <div style={{ color: '#283A50', fontSize: 8, fontFamily: 'monospace', marginTop: 2 }}>{rel.source}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Breach Pivot Chain */}
      {effectiveCluster && (
        <PivotChain cluster={effectiveCluster} />
      )}

      {/* Full Identifier Cluster */}
      {effectiveCluster && effectiveCluster.variants.length > 0 && (
        <IdentifierClusterGrid cluster={effectiveCluster} onDiscard={onDiscard} />
      )}

      {/* Empty state */}
      {!identity && passportVariants.length === 0 && relations.length === 0 && (
        <div
          style={{
            background: '#0C1118',
            border: '1px solid #182030',
            borderRadius: 8,
            padding: '32px',
            textAlign: 'center',
          }}
        >
          <span style={{ color: '#283A50', fontSize: 11, fontFamily: 'monospace' }}>
            No identity records extracted from pipeline
          </span>
        </div>
      )}
    </motion.div>
  );
}
